import { useEffect, useState } from "react";
import axiosInstance from "../../config/axiosInstance";
import { useAppSetting } from "../../context/appSettingContext";
import { useAuth } from "../../context/userContext";
import "../../css/fontawesome.css";
import useAlert from "../../hooks/Alert";
import Alert from "../Auth/Alert/Alert";
import AnimatedPage from "../FramerMotion/AnimatedPage";
import PageTitle from "../Misc/PageTitle";
import Navbar from "../Navbar/Navbar";

const formatTimeAgo = (date) => {
    const now = new Date();
    const past = new Date(date);

    const seconds = Math.floor((now - past) / 1000);

    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(seconds / 3600);
    const days = Math.floor(seconds / 86400);
    const weeks = Math.floor(seconds / 604800);
    const months = Math.floor(seconds / 2592000);
    const years = Math.floor(seconds / 31536000);

    if (seconds < 60) {
        return "Just now";
    }

    if (minutes < 60) {
        return `${minutes} min${minutes > 1 ? "s" : ""} ago`;
    }

    if (hours < 24) {
        return `${hours} hour${hours > 1 ? "s" : ""} ago`;
    }

    if (days < 7) {
        return `${days} day${days > 1 ? "s" : ""} ago`;
    }

    if (weeks < 5) {
        return `${weeks} week${weeks > 1 ? "s" : ""} ago`;
    }

    if (months < 12) {
        return `${months} month${months > 1 ? "s" : ""} ago`;
    }

    return `${years} year${years > 1 ? "s" : ""} ago`;
};

function Notifications({ baseURL, clientID }) {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const { authUser } = useAuth();
    const { appSettings } = useAppSetting();
    const {
        showAlert,
        setShowAlert,
        alertStyle,
        setAlertStyle,
        alertMessage,
        setAlertMessage,
        hideAlert,
    } = useAlert();

    useEffect(() => {
        getNotifications();
    }, []);

    const getNotifications = async () => {
        const payLoad = {
            DATA: {
                type: "NOTIFICATION",
                client_id: clientID,
                // client_id: "80623",
                card_no: authUser?.CARD_NO,
                mobile: authUser?.MOBILE,
                cust_code: authUser?.CUST_CODE,
            },
        };

        try {
            setLoading(true);
            const response = await axiosInstance.post(
                `${appSettings.AppBaseURL1}Loy_GetNotifications`,
                payLoad,
            );

            if (response?.data) {
                setNotifications(response.data);
            } else {
                console.error(
                    "Failed to fetch notifications:",
                    response.data?.MESSAGE,
                );
                setAlertStyle({
                    background:
                        "linear-gradient(to right, #f6743e 0%, #d42525 100%)",
                });
                setAlertMessage({
                    success: false,
                    message:
                        "Failed to load notifications. Please try again later.",
                    mode: "Notifications",
                });
                setShowAlert("d-block");
            }
        } catch (error) {
            console.error("Error fetching notifications:", error);
            setAlertStyle({
                background:
                    "linear-gradient(to right, #f6743e 0%, #d42525 100%)",
            });
            setAlertMessage({
                success: false,
                message:
                    "Error loading notifications. Please check your connection.",
                mode: "Notifications",
            });
            setShowAlert("d-block");
        } finally {
            setLoading(false);
        }
    };

    return (
        <AnimatedPage>
            <Alert
                showAlert={showAlert}
                alertStyle={alertStyle}
                alertMessage={alertMessage}
                hideAlert={hideAlert}
            />
            <div className="h-100 bg-wafaa-white-3">
                <div className="position-sticky z-2">
                    <div className={"transition-0-5-bounce"}>
                        <PageTitle
                            hideBackButton={false}
                            name="NOTIFICATIONS"
                        />
                    </div>
                </div>
                <div style={{ paddingBottom: "4rem" }}>
                    <div className="container py-3">
                        <div className="row justify-content-center">
                            <div className="col-12 col-md-8 col-lg-6">
                                {/* Loading State */}
                                {loading ? (
                                    <>
                                        {[1, 2, 3, 4].map((item) => (
                                            <div
                                                key={item}
                                                className="notification-card mb-3 p-3"
                                            >
                                                <div className="d-flex align-items-start gap-3">
                                                    <div className="skeleton skeleton-icon"></div>

                                                    <div className="flex-grow-1">
                                                        <div className="skeleton skeleton-title mb-2"></div>

                                                        <div className="skeleton skeleton-text mb-2"></div>

                                                        <div className="skeleton skeleton-text small"></div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </>
                                ) : notifications?.length > 0 ? (
                                    notifications.map((item) => (
                                        <div
                                            key={item.message_id}
                                            className="notification-card mb-3 p-3"
                                        >
                                            <div className="d-flex align-items-start gap-3">
                                                {/* Icon */}
                                                <div
                                                    className={`notification-card-icon ${
                                                        item.message_type ===
                                                        "new_offer"
                                                            ? "offer-icon"
                                                            : "entry-icon"
                                                    }`}
                                                >
                                                    {item.message_type ===
                                                    "new_offer" ? (
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            viewBox="0 0 640 640"
                                                            width="24"
                                                            height="24"
                                                        >
                                                            <path d="M385.5 132.8C393.1 119.9 406.9 112 421.8 112L424 112C446.1 112 464 129.9 464 152C464 174.1 446.1 192 424 192L350.7 192L385.5 132.8zM254.5 132.8L289.3 192L216 192C193.9 192 176 174.1 176 152C176 129.9 193.9 112 216 112L218.2 112C233.1 112 247 119.9 254.5 132.8zM344.1 108.5L320 149.5L295.9 108.5C279.7 80.9 250.1 64 218.2 64L216 64C167.4 64 128 103.4 128 152C128 166.4 131.5 180 137.6 192L96 192C78.3 192 64 206.3 64 224L64 256C64 273.7 78.3 288 96 288L544 288C561.7 288 576 273.7 576 256L576 224C576 206.3 561.7 192 544 192L502.4 192C508.5 180 512 166.4 512 152C512 103.4 472.6 64 424 64L421.8 64C389.9 64 360.3 80.9 344.1 108.4zM544 336L344 336L344 544L480 544C515.3 544 544 515.3 544 480L544 336zM296 336L96 336L96 480C96 515.3 124.7 544 160 544L296 544L296 336z" />
                                                        </svg>
                                                    ) : (
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            viewBox="0 0 640 640"
                                                            width="24"
                                                            height="24"
                                                        >
                                                            <path d="M320 64C302.3 64 288 78.3 288 96L288 99.2C215 114 160 178.6 160 256L160 277.7C160 325.8 143.6 372.5 113.6 410.1L103.8 422.3C98.7 428.6 96 436.4 96 444.5C96 464.1 111.9 480 131.5 480L508.4 480C528 480 543.9 464.1 543.9 444.5C543.9 436.4 541.2 428.6 536.1 422.3L526.3 410.1C496.4 372.5 480 325.8 480 277.7L480 256C480 178.6 425 114 352 99.2L352 96C352 78.3 337.7 64 320 64zM258 528C265.1 555.6 290.2 576 320 576C349.8 576 374.9 555.6 382 528L258 528z" />
                                                        </svg>
                                                    )}
                                                </div>

                                                {/* Content */}
                                                <div className="flex-grow-1 overflow-hidden">
                                                    {/* Title */}
                                                    <h6 className="fw-bold mb-1 text-wafaa-black-3 text-truncate">
                                                        {item.description}
                                                    </h6>

                                                    {/* Details */}
                                                    <p className="mb-2 notification-details">
                                                        {item.details}
                                                    </p>

                                                    {/* Footer */}
                                                    <div className="d-flex align-items-center gap-3 flex-wrap small text-wafaa-grey-5">
                                                        <div className="d-flex align-items-center gap-1 flex-wrap small">
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                x="0px"
                                                                y="0px"
                                                                width="14"
                                                                height="14"
                                                                viewBox="0 0 48 48"
                                                                className="text-wafaa-grey-5"
                                                                fill="currentColor"
                                                            >
                                                                <path d="M 24 4 C 12.972066 4 4 12.972074 4 24 C 4 35.027926 12.972066 44 24 44 C 35.027934 44 44 35.027926 44 24 C 44 12.972074 35.027934 4 24 4 z M 24 7 C 33.406615 7 41 14.593391 41 24 C 41 33.406609 33.406615 41 24 41 C 14.593385 41 7 33.406609 7 24 C 7 14.593391 14.593385 7 24 7 z M 23.476562 11.978516 A 1.50015 1.50015 0 0 0 22 13.5 L 22 25.5 A 1.50015 1.50015 0 0 0 23.5 27 L 31.5 27 A 1.50015 1.50015 0 1 0 31.5 24 L 25 24 L 25 13.5 A 1.50015 1.50015 0 0 0 23.476562 11.978516 z"></path>
                                                            </svg>
                                                            <span>
                                                                {formatTimeAgo(
                                                                    item.message_date,
                                                                )}
                                                            </span>
                                                        </div>

                                                        <div className="offer-badge">
                                                            <div className="d-flex align-items-center gap-1 flex-wrap small">
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    width="16"
                                                                    height="16"
                                                                    viewBox="0 0 24 24"
                                                                    fill="currentColor"
                                                                >
                                                                    <path d="M21 7V5H3v2a2 2 0 0 1 0 4v2a2 2 0 0 1 0 4v2h18v-2a2 2 0 0 1 0-4v-2a2 2 0 0 1 0-4z" />

                                                                    <circle
                                                                        cx="9"
                                                                        cy="9"
                                                                        r="1"
                                                                        fill="white"
                                                                    />
                                                                    <circle
                                                                        cx="15"
                                                                        cy="15"
                                                                        r="1"
                                                                        fill="white"
                                                                    />
                                                                    <line
                                                                        x1="9"
                                                                        y1="15"
                                                                        x2="15"
                                                                        y2="9"
                                                                        stroke="white"
                                                                        stroke-width="1.5"
                                                                    />
                                                                </svg>
                                                                <span className="small">
                                                                    #
                                                                    {
                                                                        item.offerId
                                                                    }
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="empty-notification text-center py-5">
                                        <div className="empty-icon mb-3">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 640 640"
                                                width="24"
                                                height="24"
                                            >
                                                <path d="M320 64C302.3 64 288 78.3 288 96L288 99.2C215 114 160 178.6 160 256L160 277.7C160 325.8 143.6 372.5 113.6 410.1L103.8 422.3C98.7 428.6 96 436.4 96 444.5C96 464.1 111.9 480 131.5 480L508.4 480C528 480 543.9 464.1 543.9 444.5C543.9 436.4 541.2 428.6 536.1 422.3L526.3 410.1C496.4 372.5 480 325.8 480 277.7L480 256C480 178.6 425 114 352 99.2L352 96C352 78.3 337.7 64 320 64zM258 528C265.1 555.6 290.2 576 320 576C349.8 576 374.9 555.6 382 528L258 528z" />
                                            </svg>
                                        </div>

                                        <h5 className="fw-bold mb-2">
                                            No Notifications
                                        </h5>

                                        <p className="text-wafaa-grey-5 mb-0">
                                            You don’t have any notifications
                                            yet.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Navbar activePage="home" />
        </AnimatedPage>
    );
}

export default Notifications;
